import { api } from './api';

export interface DailyStats {
  date: string;
  sessions: number;
  conversions: number;
  bounce_rate: number;
  avg_time_on_page: number;
}

export interface Session {
  id: string;
  session_id: string;
  language: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  referrer?: string;
  device_type?: string;
  converted: number;
  conversion_type?: string;
  started_at: string;
  posthog_session_id?: string | null;
  // "Das waren wir": true, wenn im selben Browser ein Agency-OS-Login gefunden wurde
  // (leitkraft.de und leitkraft.de/app teilen sich localStorage, siehe ConsentBanner.tsx
  // Gegenstueck im Marketing-Repo).
  is_internal?: number;
  // "Das waren echte Leads": nur gesetzt, wenn utm_content auf eine personalisierte
  // Fraud-Seite (lead_seiten) passt -- siehe api/routers/lp_analytics.py::lp_sessions.
  lead_id?: number | null;
  lead_company?: string | null;
  events?: Array<{ event_type: string; event_data?: Record<string, unknown>; created_at: string }>;
}

// Der Praefix bis vor die Session-ID, so wie er sich aus der eigenen PostHog-Cloud-Projekt-URL
// ergibt (z. B. "https://eu.posthog.com/project/12345/replay") -- 1:1 aus dem Browser kopiert,
// sobald das Projekt existiert (siehe Teil B). Kein geratenes URL-Schema hier: PostHog haengt die
// Projekt-ID mit ein, die es vor der Kontoerstellung noch nicht gibt. Ohne Wert bleibt der
// Recording-Link aus.
const POSTHOG_REPLAY_URL_PREFIX = import.meta.env.VITE_POSTHOG_REPLAY_URL_PREFIX as string | undefined;

export function posthogRecordingUrl(posthogSessionId: string): string | null {
  return POSTHOG_REPLAY_URL_PREFIX ? `${POSTHOG_REPLAY_URL_PREFIX}/${posthogSessionId}` : null;
}

export interface ABVariant {
  id: string;
  variant_id: string;
  name: string;
  element: string;
  content: string;
  language: string;
  status: 'active' | 'inactive';
  impressions: number;
  conversions: number;
  conversion_rate: number;
  thompson_probability?: number;
  created_at: string;
}

export interface BridgeRow {
  niche: string;
  sessions: number;
  conversions: number;
  conversion_rate: number;
  top_rewrite_id?: string | number;
}

export interface CreateVariantInput {
  variant_id: string;
  name: string;
  element: string;
  content: string;
  language?: string;
}

export interface StatsResponse {
  daily: DailyStats[];
  totals: {
    sessions: number;
    conversions: number;
    bounce_rate: number;
    avg_time_on_page: number;
    traffic_sources: Record<string, number>;
    device_split: Record<string, number>;
  };
}

export interface SessionsResponse {
  sessions: Session[];
  total: number;
  page: number;
}

export interface ABTestsResponse {
  variants: ABVariant[];
}

export const lpAnalyticsApi = {
  getStats: (start: string, end: string) =>
    api.get<StatsResponse>(`/lp-analytics/stats?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`),

  getSessions: (page: number) =>
    api.get<SessionsResponse>(`/lp-analytics/sessions?page=${page}`),

  getABTests: () =>
    api.get<ABTestsResponse>('/lp-analytics/ab'),

  createVariant: (data: CreateVariantInput) =>
    api.post<ABVariant>('/lp-analytics/ab/variants', data),

  updateVariant: (id: string, data: { status: 'active' | 'inactive' }) =>
    api.patch<ABVariant>(`/lp-analytics/ab/variants/${id}`, data),

  getBridge: (days = 30) =>
    api.get<BridgeRow[]>(`/lp-analytics/bridge?days=${days}`),
};
